/**
 * DNB Plumbing — Analytics & Conversion Tracking Module
 *
 * Handles GA4 + Google Ads conversion tracking via gtag.js.
 * Safe no-op when env vars are missing (e.g., preview environment).
 *
 * Required env vars (frontend/.env):
 *   REACT_APP_GA4_MEASUREMENT_ID            e.g. G-XXXXXXXXXX
 *   REACT_APP_GADS_CONVERSION_ID            e.g. AW-XXXXXXXXX
 *   REACT_APP_GADS_PHONE_CONVERSION_LABEL   e.g. AbCdEfGhIjKlMnOp
 *   REACT_APP_GADS_LEAD_CONVERSION_LABEL    e.g. QrStUvWxYzAaBbCc
 *
 * Debug mode: append ?debug_analytics=1 to any URL to log events to console.
 */

const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || '';
const ADS_ID = process.env.REACT_APP_GADS_CONVERSION_ID || '';
const CALL_LABEL = process.env.REACT_APP_GADS_PHONE_CONVERSION_LABEL || '';
const FORM_LABEL = process.env.REACT_APP_GADS_LEAD_CONVERSION_LABEL || '';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'];
const UTM_STORAGE_KEY = 'dnb_utm_v1';
const UTM_TTL_MS = 1000 * 60 * 60 * 24 * 30; // 30 days

const isBrowser = typeof window !== 'undefined';
const isDebug = isBrowser && (
  new URLSearchParams(window.location.search).get('debug_analytics') === '1' ||
  window.sessionStorage?.getItem('dnb_analytics_debug') === '1'
);

const log = (...args) => {
  if (isDebug) {
    // eslint-disable-next-line no-console
    console.log('[DNB Analytics]', ...args);
  }
};

const safeGtag = (...args) => {
  if (!isBrowser) return;
  if (typeof window.gtag !== 'function') {
    log('gtag not loaded — skipped', args);
    return;
  }
  log('gtag', ...args);
  window.gtag(...args);
};

/**
 * Inject gtag.js and configure GA4 + Google Ads.
 * Idempotent — safe to call multiple times.
 */
export const initAnalytics = () => {
  if (!isBrowser) return;
  if (window.__dnbAnalyticsInit) return;

  if (isDebug) {
    window.sessionStorage?.setItem('dnb_analytics_debug', '1');
  }

  if (!GA4_ID && !ADS_ID) {
    log('No GA4_ID or ADS_ID set — analytics disabled.');
    window.__dnbAnalyticsInit = true;
    return;
  }

  // Use whichever id we have to bootstrap gtag.js
  const bootstrapId = GA4_ID || ADS_ID;

  // Inject gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${bootstrapId}`;
  document.head.appendChild(script);

  // Initialise dataLayer + gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());

  // Configure each tag. Disable automatic page_view — we send manually on route change.
  if (GA4_ID) {
    window.gtag('config', GA4_ID, { send_page_view: false });
    log('GA4 configured', GA4_ID);
  }
  if (ADS_ID) {
    window.gtag('config', ADS_ID);
    log('Google Ads configured', ADS_ID);
  }

  window.__dnbAnalyticsInit = true;
};

/**
 * Install a global click delegator that fires click_to_call for any tel: link.
 * Detects the closest data-call-source for granular labeling, falls back to nearest data-testid.
 */
export const installCallTracker = () => {
  if (!isBrowser) return;
  if (window.__dnbCallTrackerInit) return;
  window.__dnbCallTrackerInit = true;

  document.addEventListener('click', (e) => {
    const anchor = e.target && e.target.closest && e.target.closest('a[href^="tel:"]');
    if (!anchor) return;
    const source =
      anchor.getAttribute('data-call-source') ||
      anchor.getAttribute('data-testid') ||
      'tel_link';
    trackClickToCall(source);
  }, true);
  log('Global tel: click tracker installed');
};

/**
 * Capture UTM/gclid params from current URL and persist in sessionStorage.
 * Call once on app mount (before any route changes).
 */
export const captureUTMs = () => {
  if (!isBrowser) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) captured[k] = v;
    });
    if (Object.keys(captured).length === 0) return;
    const payload = { ...captured, _ts: Date.now() };
    window.sessionStorage?.setItem(UTM_STORAGE_KEY, JSON.stringify(payload));
    log('UTMs captured', payload);
  } catch (e) {
    log('captureUTMs failed', e);
  }
};

/**
 * Return persisted UTM/gclid map (or empty object).
 * Use this when submitting the service request.
 */
export const getStoredUTMs = () => {
  if (!isBrowser) return {};
  try {
    const raw = window.sessionStorage?.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed._ts && Date.now() - parsed._ts > UTM_TTL_MS) {
      window.sessionStorage?.removeItem(UTM_STORAGE_KEY);
      return {};
    }
    const out = {};
    UTM_KEYS.forEach((k) => {
      if (parsed[k]) out[k] = parsed[k];
    });
    out.landing_page = parsed.landing_page;
    out.referrer = parsed.referrer;
    return out;
  } catch {
    return {};
  }
};

/**
 * One-time capture of landing page + referrer on first load (helpful for attribution).
 */
export const captureLandingContext = () => {
  if (!isBrowser) return;
  try {
    const raw = window.sessionStorage?.getItem(UTM_STORAGE_KEY);
    const existing = raw ? JSON.parse(raw) : {};
    if (!existing.landing_page) {
      existing.landing_page = window.location.pathname + window.location.search;
      existing.referrer = document.referrer || '';
      existing._ts = existing._ts || Date.now();
      window.sessionStorage?.setItem(UTM_STORAGE_KEY, JSON.stringify(existing));
      log('Landing context captured', existing);
    }
  } catch (e) {
    log('captureLandingContext failed', e);
  }
};

/**
 * Track SPA page view (call on every route change).
 */
export const trackPageView = (path) => {
  const page_path = path || (isBrowser ? window.location.pathname + window.location.search : '/');
  if (GA4_ID) {
    safeGtag('event', 'page_view', {
      page_path,
      page_location: isBrowser ? window.location.href : '',
      page_title: isBrowser ? document.title : '',
      send_to: GA4_ID,
    });
  }
};

/**
 * Track click-to-call. Fires GA4 event + Google Ads phone-call conversion.
 */
export const trackClickToCall = (source = 'unknown') => {
  if (GA4_ID) {
    safeGtag('event', 'click_to_call', {
      event_category: 'engagement',
      event_label: source,
      phone_number: '403-615-8770',
      send_to: GA4_ID,
    });
  }
  if (ADS_ID && CALL_LABEL) {
    safeGtag('event', 'conversion', {
      send_to: `${ADS_ID}/${CALL_LABEL}`,
      event_callback: () => log('Ads call conversion fired'),
    });
  }
};

/**
 * Track the first interaction with the service request form.
 * Fires only once per session (deduped via sessionStorage). GA4 only — no Ads conversion.
 */
export const trackFormStarted = (meta = {}) => {
  if (!isBrowser) return;
  try {
    if (window.sessionStorage?.getItem('dnb_form_started_v1') === '1') return;
    window.sessionStorage?.setItem('dnb_form_started_v1', '1');
  } catch {
    // sessionStorage unavailable — fall through and let it fire (worst case: duplicate event)
  }
  if (GA4_ID) {
    const utm = getStoredUTMs();
    // Emit the GA4-standard name `form_start` (Google's recommended lead-funnel event)
    // plus the legacy `form_started` alias so any existing GA4 audiences keep working.
    const payload = {
      event_category: 'lead_funnel',
      form_name: meta.form_name || 'service_request',
      send_to: GA4_ID,
      ...utm,
    };
    safeGtag('event', 'form_start', payload);
    safeGtag('event', 'form_started', payload);
  }
};

/**
 * Track photo/file selection on the service-request form. GA4 only — no Ads conversion.
 */
export const trackFileUpload = (meta = {}) => {
  if (!isBrowser) return;
  if (GA4_ID) {
    const utm = getStoredUTMs();
    safeGtag('event', 'file_upload', {
      event_category: 'lead_funnel',
      form_name: meta.form_name || 'service_request',
      file_count: meta.count || 1,
      send_to: GA4_ID,
      ...utm,
    });
  }
};

/**
 * Track successful service request submission. Fires GA4 + Google Ads form conversion.
 */
export const trackServiceRequestSubmit = (meta = {}) => {
  if (GA4_ID) {
    safeGtag('event', 'service_request_submit_success', {
      event_category: 'lead',
      event_label: meta.service_needed || 'unknown',
      request_type: meta.request_type || 'service',
      send_to: GA4_ID,
    });
    // Also fire GA4 standard 'generate_lead' for richer reporting
    safeGtag('event', 'generate_lead', {
      currency: 'CAD',
      value: 1,
      send_to: GA4_ID,
    });
  }
  if (ADS_ID && FORM_LABEL) {
    safeGtag('event', 'conversion', {
      send_to: `${ADS_ID}/${FORM_LABEL}`,
      event_callback: () => log('Ads form conversion fired'),
    });
  }
};

/**
 * Track view of the /thank-you page.
 */
export const trackThankYouPageView = () => {
  if (GA4_ID) {
    safeGtag('event', 'thank_you_page_view', {
      event_category: 'lead',
      send_to: GA4_ID,
    });
  }
};

export const __analyticsConfig = { GA4_ID, ADS_ID, CALL_LABEL, FORM_LABEL };
